import { Box, useTheme } from '@mui/material';
import { PropsWithChildren, forwardRef } from 'react';

import { Icon } from '@verantos-dev/shared-components';

export type CohortBuilderEditModalFilterProps = {
  /**
   * @prop {string} - id of the filter component
   */
  id: string;

  /**
   * @prop {boolean} disableRemove - whether or not the remove button is enabled
   * @default false
   */
  disableRemove?: boolean;

  /**
   * @prop {function} - function to call when the remove filter button is clicked
   * @param {string} - id of the filter component
   */
  onRemoveFilter?: (id: string) => void;

  /**
   * @prop {boolean} - to view component in view mode
   * @default false
   */
  viewMode?: boolean;
};

export const CohortBuilderEditModalFilter = forwardRef<
  HTMLDivElement,
  PropsWithChildren<CohortBuilderEditModalFilterProps>
>((props, ref) => {
  const { id, children, disableRemove = false, onRemoveFilter, viewMode = false, ...rest } = props;

  const theme = useTheme();

  const onClickHandler = () => {
    if (!disableRemove) {
      onRemoveFilter && onRemoveFilter(id);
    }
  };

  return (
    <Box
      {...rest}
      ref={ref}
      data-testid={`cohortBuilderEditModalFilter-${id}`}
      sx={{
        display: 'flex',
        fleDirection: 'row',
        justifyContent: 'start',
        alignItems: 'center',
        minHeight: theme.spacing(20),
        width: 'inherit',
        borderRadius: theme.spacing(4),
        backgroundColor: theme.palette.cardBackground?.main,
        padding: theme.spacing(4),
      }}
    >
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'row',
          justifyContent: 'space-between',
          alignItems: 'center',
          width: '100%',
          gap: theme.spacing(4),
        }}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'row',
            gap: theme.spacing(4),
            width: !onRemoveFilter ? '100%' : 'calc(100% - 56px)',
          }}
        >
          {children}
        </Box>
        {!viewMode && onRemoveFilter && (
          <Box
            sx={[
              {
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                width: theme.spacing(10),
                height: theme.spacing(10),
                minHeight: theme.spacing(10),
                minWidth: theme.spacing(10),
                borderRadius: theme.spacing(1),
                cursor: 'pointer',
              },
              disableRemove
                ? { cursor: 'default', '&:hover': { backgroundColor: 'transparent' } }
                : { '&:hover': { backgroundColor: theme.palette.text.disabled } },
            ]}
            onClick={onClickHandler}
          >
            <Icon
              id={`cohortBuilderEditModalFilter-removeFilterBtn-${id}`}
              icon={disableRemove ? 'clearDisabled' : 'clear'}
              width={16}
              height={16}
            />
          </Box>
        )}
      </Box>
    </Box>
  );
});
